import React from "react";
import { IManageNoticesProps } from "./ManageNotices.types";
import {
  useDispatchWrapper,
  usePoliciesByCustomer,
} from "../../hooks";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import {
  Box,
  Button,
  Grid,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../components/formsComponents";
import { CustomerAutoSearch } from "../../components/AutoCompleteSearches/CustomerAutoSearch";
import { UsersAutoSearchByRole } from "../../components/AutoCompleteSearches/UsersAutoSearchByRole";
import {
  CustomDatePicker,
  CustomTimePicker,
} from "../../components/formsComponents/CustomDatePicker";
import { FileUpload } from "../../features/FileUpload/components/FileUpload";
import {
  fetchAllCustomerDetailsForNotices,
  upsertNoticeAndEdocAsync,
  upsertNoticesAysnc,
} from "../../redux/notices/noticesActions";
import { useCurrentUserId } from "../../hooks/useAssignedTo";
import produce from "immer";
import {
  ERROR_MESSAGES,
  POLICIES,
} from "../../constants/enums";
import { RightPanel } from "../../components/RightPanel";
import { CustomAlert } from "../../components/formsComponents/CustomAlert";
import { ICustomerAllPolicyDetail } from "../../redux/notices/notices.types";
import {
  IDataTableProps,
  RenderType,
} from "../../components/Table/interfaces/IDataTableProps";
import { StatusRenderer } from "../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { DataTable } from "../../components/Table/DataTable";
import { ExtendableSelect } from "../../components/formsComponents/ExtendableSelect";
import { getInsurerDataByCode } from "../../helpers/getNameByCode";
import { InsurerCodeV2AutoSearch } from "../../components/AutoCompleteSearches/InsurerCodeV2AutoSearch";

export const ManageNotices: React.FC<IManageNoticesProps> = (props) => {
  const {
    open,
    onClose,
    onComplete,
    data,
    customerId,
    customerpolicyId,
    useUnifiedUpsert,
  } = props;
  const [file, setFile] = React.useState<File | null>(null);
  const [saveLoading, setSaveLoading] = React.useState(false);

  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();
  const currentUser = useCurrentUserId();
  const [searchValue, setSearchvalue] = React.useState("");
  const [searchError, setSearchError] = React.useState<string | null>(null);
  const [searchResults, setSearchResults] = React.useState<{
    list: ICustomerAllPolicyDetail[];
    totalRecords: number;
  }>({
    list: [],
    totalRecords: 0,
  });
  const isUpdate = data.notice_and_edoc_code ? true : false;
  const { policiesList, policiesLoading, fetchPoliciesByCustomerId } =
    usePoliciesByCustomer();
  const [tablePagination, setTablePagination] = React.useState({
    pageNumber: 1,
    rowsInPerPage: 5,
  });
  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
  } = useFormik({
    initialValues: {
      ...data,
      customer_id: customerId as any,
      customer_policy_id: customerpolicyId as any,
      assigned_to_id: data.assigned_to_id || currentUser,
    },
    validate: (values) => {
      const errors: any = {};
      if (!values.customer_id && values.notice_type !== "Updates") {
        errors.customer_id = "*Customer name is required";
      }
      return errors;
    },
    onSubmit: async (values) => {
      setSaveLoading(true);
      const isUpdate = data.notice_and_edoc_code ? true : false;
      const handleResult = (isSuccess: boolean) => {
        if (isSuccess) {
          // navigate("/notices");
          onComplete();
        }
        setSaveLoading(false);
      };
      if (useUnifiedUpsert) {
        dispatch(
          upsertNoticeAndEdocAsync(
            { ...values },
            { ...data },
            "NOTICE",
            isUpdate,
            file,
            handleResult
          )
        );
      } else {
        dispatch(
          upsertNoticesAysnc(
            { ...values },
            {
              ...data,
            },
            isUpdate,
            file,
            handleResult
          )
        );
      }
    },
  });

  const handleSearch = () => {
    setSearchError(null);
    setSearchResults({ list: [], totalRecords: 0 });
    if (searchValue) {
      setValues({
        ...values,
        customer_id: null,
        policy_number: null,
        branch_code: null,
        // csr_code: firstMatchedData.csr_code,
        line_of_business: null,
        producer_code: null,
      });
      dispatch(
        fetchAllCustomerDetailsForNotices(
          tablePagination.pageNumber,
          tablePagination.rowsInPerPage,
          searchValue,
          (data, totalRecords) => {
            if (data.length > 0) {
              setSearchResults({
                list: data,
                totalRecords: totalRecords,
              });
              // setValues({
              //   ...values,
              //   customer: firstMatchedData.customer_id as any,
              //   policy_number: firstMatchedData.policy_number,
              //   branch_code: firstMatchedData.branch_code,
              //  // csr_code: firstMatchedData.csr_code,
              //   line_of_business: firstMatchedData.line_of_business,
              //   producer_code: firstMatchedData.producer_one_code,
              // })
            } else {
              setSearchError(ERROR_MESSAGES.NO_RECORD_FOUND);
            }
          }
        )
      );
    } else {
      setSearchError("Search field value is requried!");
    }
  };

  React.useEffect(() => {
    const setPolicyData = async () => {
      if (customerpolicyId && policiesList.length > 0) {
        const policy = policiesList.find(
          (x) => x.customer_policy_id === Number(customerpolicyId)
        );
  
        if (policy) {
  
          setValues((prevValues) => ({
            ...prevValues,
            customer_policy_id: customerpolicyId,
            policy_number: policy.policy_number,
            branch_code: policy.branch_code,
            csr_code: policy.csr_code,
            policy_type: policy.line_of_business,
            producer_code: policy.producer_one_code,
          }));
        }
      } else if (isUpdate) {
        setValues(data);
      }
    };
  
    setPolicyData();
  }, [customerpolicyId, policiesList, data, isUpdate]);

  const handlePolicyChange = async (e: SelectChangeEvent<unknown>) => {
    const policyId = e.target.value as string;
    const policy = policiesList.find(
      (x) => x.customer_policy_id === Number(policyId)
    );
    if (policy) {
      const insurer = await getInsurerDataByCode(policy.insurer);
      setValues({
        ...values,
        customer_policy_id: policyId,
        policy_number: policy.policy_number,
        branch_code: policy.branch_code,
        csr_code: policy.csr_code,
        policy_type: policy.line_of_business,
        producer_code: policy.producer_one_code,
        insurer_id: insurer ? insurer.insurer_code: null,
        insurance_company: insurer ? insurer.name: null,
        contract_term_effective_date: policy.effective_date,
        contract_term_expiry_date: policy.expiry_date,
        producer_one_id: policy.producer_one
        
      });
    }
  };

  const handleTypeChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const newState = produce(values, (draftState) => {
      draftState.notice_type = value;
      // if (value === "Updates") {
      //   draftState.customer_id = null;
      //   draftState.policy_number = null;
      //   draftState.line_of_business = null;
      //   draftState.producer_code = null;
      //   draftState.csr_code = null;
      //   draftState.branch_code = null;
      //   draftState.action_taken = null;
      // } else {
      //   draftState.policy_type = null;
      //   draftState.insurer_id = null;
      // }
    });
    setValues(newState);
  };

  const tableProps: IDataTableProps = {
    isPagination: true,
    isDataLoading: false,
    rowsPerPageOptions: tablePagination.rowsInPerPage,
    totalRecords: searchResults.totalRecords,
    columns: [
      {
        key: "policy_status",
        headerName: "Policy Status",
        fieldName: "",
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row) => {
          return <StatusRenderer status={row.policy_status} />;
        },
      },
      {
        key: "policy_number",
        headerName: "Policy Number",
        fieldName: "policy_number",
        renderType: RenderType.TEXT_DARK,
      },
      {
        key: "customer_name",
        headerName: "Customer Name",
        fieldName: "customer_name",
        renderType: RenderType.TEXT,
      },
      {
        key: "customer_code",
        headerName: "Customer Code",
        fieldName: "customer_code",
        renderType: RenderType.TEXT,
      },
      {
        key: "line_of_business",
        headerName: "Line of Business",
        fieldName: "line_of_business",
        renderType: RenderType.TEXT,
      },
      {
        key: "branch_code",
        headerName: "Branch Code",
        fieldName: "branch_code",
        renderType: RenderType.TEXT,
      },
      {
        key: "effective_date",
        headerName: "Effective Date",
        fieldName: "effective_date",
        renderType: RenderType.DATE,
      },
      {
        key: "expiry_date",
        headerName: "Expiry Date",
        fieldName: "expiry_date",
        renderType: RenderType.TEXT,
      },
      {
        key: "select",
        headerName: "Select",
        fieldName: "select",
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row) => {
          return (
            <Button
              variant="contained"
              onClick={() => {
                setSearchResults({ list: [], totalRecords: 0 });
                setValues({
                  ...values,
                  customer_id: row.customer_id as any,
                  customer_name: row.customer_name,
                  policy_number: row.policy_number,
                  branch_code: row.branch_code,
                  // csr_code: row.csr_code,
                  line_of_business: row.line_of_business,
                  producer_code: row.producer_one_code,
                  producer_one_id: row.producer_one,
                  customer_policy_id: row.customer_policy_id,
                });
              }}
            >
              Select
            </Button>
          );
        },
      },
    ],
    items: searchResults.list,
    onPageChange: (newPageNumber: number) => {
      dispatch(
        fetchAllCustomerDetailsForNotices(
          newPageNumber,
          tablePagination.rowsInPerPage,
          searchValue,
          (data, totalRecords) => {
            setSearchResults({
              list: data,
              totalRecords: totalRecords,
            });
          }
        )
      );
      setTablePagination({ ...tablePagination, pageNumber: newPageNumber });
    },
    onRowsPerPageChange: (pageNumber: number, rowsPerPage: number) => {
      dispatch(
        fetchAllCustomerDetailsForNotices(
          pageNumber,
          rowsPerPage,
          searchValue,
          (data, totalRecords) => {
            setSearchResults({
              list: data,
              totalRecords: totalRecords,
            });
          }
        )
      );
      setTablePagination({
        pageNumber: pageNumber,
        rowsInPerPage: rowsPerPage,
      });
    },
  };

  React.useEffect(() => {
    if (values.customer_id) {
      fetchPoliciesByCustomerId(values.customer_id as number);
    }
  }, [values.customer_id]);

  return (
    <RightPanel
      open={open}
      heading="Create/Edit"
      onClose={onClose}
      width="80%"
      isWrappedWithForm
      onFormSubmit={handleSubmit}
      actionButtons={() => {
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={2}>
              <Button variant="contained" type="submit" fullWidth>
                Save
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="contained" fullWidth onClick={onClose}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        );
      }}
    >
      {searchError && (
        <CustomAlert severity="error" onClose={() => setSearchError(null)}>
          {searchError}
        </CustomAlert>
      )}
      <Grid container spacing={1}>
        <Grid item xs={12} md={3.6}>
          <CustomFormLabel>Search</CustomFormLabel>
          <Stack direction={"row"} alignItems={"start"} spacing={1}>
            <Box>
              <CustomTextField
                fullWidth
                value={searchValue}
                onChange={(e) => setSearchvalue(e.target.value)}
              />
              <Typography variant="caption">
                (By Customer Name, Customer Code, Policy Number)
              </Typography>
            </Box>
            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>
          </Stack>
        </Grid>
        {searchResults.list.length === 0 && <Grid item xs={7.4} />}
        {searchResults.list.length > 0 && (
          <Grid item xs={12} mt={1}>
            <DataTable {...tableProps} />
          </Grid>
        )}

        <Grid item xs={12} lg={3}>
          <CustomFormLabel>Type</CustomFormLabel>
          <ControlledCustomSelect
            name="notice_type"
            fullWidth
            value={values.notice_type}
            options={[
              { label: "Updates", value: "Updates" },
              { label: "Cancellation Notice", value: "Cancellation Notice" },
              { label: "Claim Notice", value: "Claim Notice" },
              {
                label: "Coverage Change Notice",
                value: "Coverage Change Notice",
              },
              { label: "Incorrect Address", value: "Incorrect Address" },
              { label: "Payment Notice", value: "Payment Notice" },
              { label: "Non-Renewal Notice", value: "Non-Renewal Notice" },
              { label: "Underwriting Notice", value: "Underwriting Notice" },
              {
                label: "Agency Cancellation Advice",
                value: "Agency Cancellation Advice",
              },
              {
                label: "NSF Special withdrawal notice",
                value: "NSF Special withdrawal notice",
              },
              {
                label: "Non-NSF special withdrawal notice",
                value: "Non-NSF special withdrawal notice",
              },
              {
                label: "Non-Pay Cancellation Non-Reinstatement Notification",
                value: "Non-Pay Cancellation Non-Reinstatement Notification",
              },
              {
                label:
                  "Request for New Payment Information for a delinquent policy",
                value:
                  "Request for New Payment Information for a delinquent policy",
              },
              { label: "Varied Terms Letter", value: "Varied Terms Letter" },
              {
                label: "Cancellation special withdrawal notice",
                value: "Cancellation special withdrawal notice",
              },
              {
                label: "Please Update Customer Mailing Address",
                value: "Please Update Customer Mailing Address",
              },
              {
                label: "Commercial Past Due Payment",
                value: "Commercial Past Due Payment",
              },
              { label: "Collection notice", value: "Collection notice" },
              {
                label: "Agency Cancellation Advice",
                value: "Agency Cancellation Advice",
              },
              { label: "Cancellation Notice", value: "Cancellation Notice" },
              { label: "Claim notice", value: "Claim notice" },
              { label: "Non-Renewal notice", value: "Non-Renewal notice" },
              { label: "Underwriting notice", value: "Underwriting notice" },
              { label: "Broker memo", value: "Broker memo" },
              { label: "Metrofax message", value: "Metrofax message" },
              { label: "Payment notice", value: "Payment notice" },
              {
                label: "Initial Adjuster Assignment",
                value: "Initial Adjuster Assignment",
              },
              {
                label: "Credit Card Expiry Notification",
                value: "Credit Card Expiry Notification",
              },
              { label: "Email from client", value: "Email from client" },
              { label: "Other", value: "Other" },
            ]}
            onChange={handleTypeChange}
            displayEmpty
            placeholder="Select one"
          />
        </Grid>
        {/* {values.notice_type !== "Updates" && ( */}
          <>
            <Grid item xs={12} md={3}>
              <CustomerAutoSearch
                label="Customer Name"
                value={Number(values.customer_id)}
                onSelect={(newValue) => {
                  setValues({
                    ...values,
                    customer_id: newValue.customer_id as any,
                    customer_name: newValue.customer_name,
                    policy_number: null,
                  });
                }}
                disabled={!!(customerId && customerpolicyId)}
              />
              {/* {errors.customer_id && (
                <ErrorMessage value={errors.customer_id as string} />
              )} */}
            </Grid>
            <Grid item xs={12} md={3}>
              <CustomFormLabel>Policy Number</CustomFormLabel>
              <ControlledCustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="customer_policy_id"
                fullWidth
                displayEmpty
                value={Number(values.customer_policy_id)}
                onChange={handlePolicyChange}
                placeholder="Select One"
                options={policiesList.map((item) => {
                  return {
                    label: item.policy_number,
                    value: item.customer_policy_id,
                  };
                })}
                disabled={!!(customerId && customerpolicyId)}
              />
            </Grid>
            {/* <Grid item xs={12} md={3}>
              <CustomFormLabel>Line of Business</CustomFormLabel>
              <CustomTextField
                id="policy_type"
                fullWidth
                disabled
                value={values.policy_type}
                onChange={handleChange}
              />
            </Grid> */}
             <Grid item xs={12} lg={3}>
              <CustomFormLabel>Policy Type</CustomFormLabel>
              <ControlledCustomSelect
                name="policy_type"
                fullWidth
                value={values.policy_type}
                displayEmpty
                placeholder="Select one"
                onChange={handleChange}
                options={[
                  {
                    label: POLICIES.BUSINESS_POLICY,
                    value: POLICIES.BUSINESS_POLICY,
                  },
                  {
                    label: POLICIES.COMMERCIAL_AUTO_POLICY,
                    value: POLICIES.COMMERCIAL_AUTO_POLICY,
                  },
                  {
                    label: POLICIES.AUTOMOBILE_POLICY,
                    value: POLICIES.AUTOMOBILE_POLICY,
                  },

                  {
                    label: POLICIES.HABITATIONAL_POLICY,
                    value: POLICIES.HABITATIONAL_POLICY,
                  },
                  {
                    label: POLICIES.LIFE_AND_FINCANCIAL_POLICY,
                    value: POLICIES.LIFE_AND_FINCANCIAL_POLICY,
                  },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <CustomFormLabel>Producer</CustomFormLabel>
              <CustomTextField
                id="producer"
                fullWidth
                disabled
                value={values.producer_code}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <CustomFormLabel>CSR</CustomFormLabel>
              <CustomTextField
                id="csr"
                fullWidth
                disabled
                value={values.csr_code}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <CustomFormLabel>Branch</CustomFormLabel>
              <CustomTextField
                id="branch"
                fullWidth
                disabled
                value={values.branch_code}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} lg={3}>
              <CustomFormLabel>Insurer</CustomFormLabel>
              <InsurerCodeV2AutoSearch
                value={values.insurer_id}
                onSelect={(value) => {
                  // setFieldValue("insurer_id", value.value);
                  setValues({
                    ...values,
                    insurer_id: value.insurer_code,
                    insurance_company: value.name,
                  });

                }}
              />
            </Grid>

            <Grid item xs={12} md={2.4}>
              <CustomFormLabel>Action Taken</CustomFormLabel>
              <ExtendableSelect
                value={values.action_taken}
                onChange={(newValue) => setFieldValue("action_taken", newValue)}
                placeholder="Select one"
                options={[
                  "Discussed with customer",
                  "Left Voice Mail",
                  "Sent Text Message",
                  "Sent Whatsapp Message",
                ].map((consent) => {
                  return { label: consent, value: consent };
                })}
              ></ExtendableSelect>
            </Grid>
          </>
        {/* )} */}
        <Grid item xs={12} md={2.4}>
          <CustomFormLabel>Status</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={values.status}
            name="status"
            onChange={handleChange}
            placeholder="Status"
            displayEmpty
            options={
              [
                 { label: "Open", value: "Open" },
              { label: "Closed", value: "Closed" },
              ]
            }
          ></ControlledCustomSelect>
        </Grid>

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Date Created</CustomFormLabel>
          <CustomDatePicker
            fullWidth
            disabled
            value={values.create_ts || ""}
            onChange={(newValue) => {
              setFieldValue("create_ts", newValue);
            }}
          />
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Due Date</CustomFormLabel>
          <CustomDatePicker
            fullWidth
            value={values.due_date || ""}
            onChange={(newValue) => {
              setFieldValue("due_date", newValue);
            }}
          />
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Time</CustomFormLabel>
          <CustomTimePicker
            fullWidth
            value={values.due_time}
            onChange={(newValue) => {
              setFieldValue("due_time", newValue);
            }}
          />
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Effective Date</CustomFormLabel>
          <CustomDatePicker
            fullWidth
            value={values.contract_term_effective_date || ""}
            onChange={(newValue) => {
              setFieldValue("contract_term_effective_date", newValue);
            }}
          />
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Expiry Date</CustomFormLabel>
          <CustomDatePicker
            fullWidth
            value={values.contract_term_expiry_date || ""}
            onChange={(newValue) => {
              setFieldValue("contract_term_expiry_date", newValue);
            }}
          />
        </Grid>
       

        <Grid item xs={12} md={2.5}>
          <UsersAutoSearchByRole
            label="Assigned To"
            value={values.assigned_to_id}
            onSelect={(data) => {
              setFieldValue("assigned_to_id", data.user_fact_id);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>Mobile Number</CustomFormLabel>
          <CustomTextField
            id="mobile_number"
            fullWidth
            value={values.mobile_number}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>Email</CustomFormLabel>
          <CustomTextField
            id="email"
            fullWidth
            value={values.email}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>Message Type</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={values.message_type}
            name="message_type"
            onChange={handleChange}
            placeholder="Select one"
            displayEmpty
            options={["eDocs", "Notices"].map((consent) => {
              return { label: consent, value: consent };
            })}
          ></ControlledCustomSelect>
        </Grid>
        <Grid item xs={12} lg={12}>
          <CustomFormLabel>Description</CustomFormLabel>
          <CustomTextField
            name="description"
            variant="outlined"
            size="small"
            type="text"
            multiline
            rows={2}
            fullWidth
            value={values.description}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} lg={3}>
          <CustomFormLabel>File Upload</CustomFormLabel>
          <FileUpload
            value={values.attachment}
            onChange={(newFile) => {
              setFile(newFile);
            }}
            onDelete={() => {
              setFile(null);
              setFieldValue("attachment", null);
            }}
          />
        </Grid>
      </Grid>
    </RightPanel>
  );
};
